import type { FilterState, SortKey } from "@/utils/filmQuery";
import { emptyFilters, ratingOptions, sortOptions, yearOptions } from "@/utils/filmQuery";
import { genres, regions } from "@/mocks/films";

type FilterPanelProps = {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  sort: SortKey;
  onSortChange: (next: SortKey) => void;
  hideSort?: boolean;
};

type GroupProps = {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
};

const Group = ({ label, options, value, onSelect }: GroupProps) => (
  <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
    <span className="shrink-0 text-xs font-medium uppercase tracking-[0.14em] text-foreground-400 md:w-16">
      {label}
    </span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option === value;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
              active
                ? "bg-primary-500 font-medium text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

const FilterPanel = ({ filters, onChange, sort, onSortChange, hideSort = false }: FilterPanelProps) => {
  const update = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-4">
        <Group
          label="类型"
          options={["全部", ...genres]}
          value={filters.genre}
          onSelect={(genre) => update({ genre })}
        />
        <div className="h-px w-full bg-background-200/70" />
        <Group
          label="地区"
          options={regions}
          value={filters.region}
          onSelect={(region) => update({ region })}
        />
        <div className="h-px w-full bg-background-200/70" />
        <Group
          label="年份"
          options={yearOptions}
          value={filters.year}
          onSelect={(year) => update({ year })}
        />
        <div className="h-px w-full bg-background-200/70" />
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Group
            label="评分"
            options={ratingOptions}
            value={filters.rating}
            onSelect={(rating) => update({ rating })}
          />
          {hideSort ? null : (
            <div className="flex items-center gap-2 self-end">
              <span className="text-xs text-foreground-400">排序</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(event) => onSortChange(event.target.value as SortKey)}
                  aria-label="排序方式"
                  className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 outline-none focus:border-primary-400"
                >
                  {sortOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
                  <i className="ri-arrow-down-s-line" />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3">
        <span className="text-[11px] text-foreground-400">筛选条件即时生效</span>
        <button
          type="button"
          onClick={() => onChange(emptyFilters)}
          className="flex cursor-pointer items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-refresh-line" />
          </span>
          重置筛选
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;